import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { api, formatApiError, formatIDR, todayStr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const emptyForm = (driverId = "") => ({ trip_date: todayStr(), tour_id: "", pax: 0, extras: [], notes: "", driver_id: driverId, aqua: false, aqua_qty: 0, susuk: 0, collect: 0 });

function AquaSusuk({ form, set, aquaPrice }) {
  const toggleAqua = (v) => {
    set("aqua", !!v);
    if (v && !Number(form.aqua_qty)) set("aqua_qty", Number(form.pax) || 0);
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4 rounded-xl border border-slate-200 p-4">
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer min-h-[36px]">
          <Checkbox checked={form.aqua} onCheckedChange={toggleAqua} data-testid="trip-aqua-checkbox" />
          Service Aqua <span className="font-num text-xs text-slate-500">{formatIDR(aquaPrice)}/pax</span>
        </label>
        {form.aqua && (
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Jumlah Aqua (pax)</Label>
            <Input type="number" min={0} className="font-num" value={form.aqua_qty} onChange={(e) => set("aqua_qty", e.target.value)} data-testid="trip-aqua-qty-input" />
          </div>
        )}
      </div>
      <div className="space-y-1">
        <Label>Susuk (Rp, opsional)</Label>
        <Input type="number" min={0} className="font-num" placeholder="0" value={form.susuk || ""} onChange={(e) => set("susuk", Number(e.target.value) || 0)} data-testid="trip-susuk-input" />
        <p className="text-[11px] text-slate-500">Dicatat di report harian, tidak masuk total bayar.</p>
      </div>
    </div>
  );
}

function Line({ label, value, strong, testId }) {
  return (
    <div className={`flex justify-between gap-3 ${strong ? "pt-3 mt-1 border-t border-white/10" : ""}`}>
      <span className={strong ? "font-semibold" : "text-slate-400"}>{label}</span>
      <span data-testid={testId} className={`font-num ${strong ? "text-emerald-400 text-lg font-bold" : ""}`}>{value}</span>
    </div>
  );
}

function ExtrasEditor({ extras, onChange }) {
  const update = (i, key, val) => onChange(extras.map((e, idx) => (idx === i ? { ...e, [key]: val } : e)));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Additional Extra (opsional)</Label>
        <button type="button" onClick={() => onChange([...extras, { description: "", amount: 0 }])} className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 min-h-[36px]" data-testid="add-extra-btn">
          <Plus className="h-3.5 w-3.5" /> Tambah Extra
        </button>
      </div>
      {extras.map((e, i) => (
        <div key={i} className="flex gap-2">
          <Input placeholder="Contoh: Parkir, Makan, Lembur" value={e.description} onChange={(ev) => update(i, "description", ev.target.value)} data-testid={`extra-desc-${i}`} />
          <Input type="number" min={0} className="w-36 font-num" placeholder="Rp" value={e.amount || ""} onChange={(ev) => update(i, "amount", Number(ev.target.value) || 0)} data-testid={`extra-amount-${i}`} />
          <button type="button" onClick={() => onChange(extras.filter((_, idx) => idx !== i))} className="h-10 w-10 shrink-0 grid place-items-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`remove-extra-${i}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

export function TripFormDialog({ open, onOpenChange, trip, onSaved, defaultDriverId = "" }) {
  const { user } = useAuth();
  const pickDriver = user?.role === "admin" && !trip;
  const isAdmin = user?.role === "admin";
  const [drivers, setDrivers] = useState([]);
  const [tours, setTours] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [aquaPrice, setAquaPrice] = useState(0);

  useEffect(() => {
    if (!open) return;
    api.get("/settings").then((r) => setAquaPrice(r.data.aqua_price));
    api.get("/tours").then((r) => setTours(r.data.filter((t) => t.active || t.id === trip?.tour_id)));
    if (pickDriver) api.get("/users").then((r) => setDrivers(r.data.filter((d) => d.active)));
    setForm(trip ? { trip_date: trip.trip_date, tour_id: trip.tour_id, pax: trip.pax, extras: trip.extras, notes: trip.notes, aqua: trip.aqua, aqua_qty: trip.aqua_qty, susuk: trip.susuk, collect: trip.collect || 0 } : emptyForm(defaultDriverId));
  }, [open, trip, pickDriver, defaultDriverId]);

  const tour = tours.find((t) => t.id === form.tour_id);
  const calc = useMemo(() => {
    const base = tour?.price_per_trip || 0;
    const ticket = (tour?.ticket_price || 0) * (Number(form.pax) || 0);
    const extras = form.extras.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const aqua = form.aqua ? aquaPrice * (Number(form.aqua_qty) || 0) : 0;
    const total = base + ticket + extras + aqua;
    const collect = Number(form.collect) || 0;
    return { base, ticket, extras, aqua, total, collect, payout: total - collect };
  }, [tour, form, aquaPrice]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (pickDriver && !form.driver_id) return toast.error("Pilih driver terlebih dahulu");
    if (!form.tour_id) return toast.error("Pilih tour terlebih dahulu");
    setSaving(true);
    try {
      const payload = { ...form, pax: Number(form.pax) || 0, aqua_qty: form.aqua ? Number(form.aqua_qty) || 0 : 0, susuk: Number(form.susuk) || 0, ...(isAdmin ? { collect: Number(form.collect) || 0 } : { collect: undefined }) };
      if (trip) await api.put(`/trips/${trip.id}`, payload);
      else await api.post("/trips", payload);
      toast.success(trip ? "Report trip diperbarui" : "Report trip tersimpan");
      onOpenChange(false);
      onSaved(payload.trip_date.slice(0, 7));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0" data-testid="trip-form-dialog">
        <form onSubmit={submit} className="grid md:grid-cols-[1fr_240px]">
          <div className="p-6 space-y-5">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">{trip?.status === "order" ? "Lengkapi Report Order" : trip ? "Edit Report Trip" : "Report Trip Baru"}</DialogTitle>
              <DialogDescription>{pickDriver ? "Buat report atas nama driver yang lupa mengisi." : "Pilih tour, isi jumlah orang dan extra jika ada."}</DialogDescription>
            </DialogHeader>
            {trip?.status === "order" && (
              <div className="rounded-xl bg-sky-50 border border-sky-100 p-3 text-xs text-slate-700 space-y-0.5" data-testid="order-summary-box">
                <p className="font-semibold text-sky-800">Detail Order dari Admin</p>
                {trip.trip_name && <p>Nama Trip: {trip.trip_name}</p>}
                {trip.booking_platform && <p>Platform: {trip.booking_platform}</p>}
                {(trip.pickup_time || trip.pickup_location) && <p>Jemput: {trip.pickup_time} {trip.pickup_location}</p>}
                {trip.guest_name && <p>Tamu: {trip.guest_name} {trip.guest_phone}</p>}
                {trip.driver_note && <p className="whitespace-pre-line max-h-40 overflow-y-auto">Catatan: {trip.driver_note}</p>}
              </div>
            )}
            {pickDriver && (
              <div className="space-y-1.5">
                <Label>Driver</Label>
                <Select value={form.driver_id} onValueChange={(v) => set("driver_id", v)}>
                  <SelectTrigger data-testid="trip-driver-select"><SelectValue placeholder="Pilih driver" /></SelectTrigger>
                  <SelectContent>
                    {drivers.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`driver-option-${d.id}`}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Tanggal Trip</Label>
                <Input type="date" required value={form.trip_date} onChange={(e) => set("trip_date", e.target.value)} data-testid="trip-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Jumlah Orang (Pax)</Label>
                <Input type="number" min={0} className="font-num" value={form.pax} onChange={(e) => setForm((f) => ({ ...f, pax: e.target.value, aqua_qty: f.aqua && Number(f.aqua_qty) === (Number(f.pax) || 0) ? Number(e.target.value) || 0 : f.aqua_qty }))} data-testid="trip-pax-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Nama Tour</Label>
              <Select value={form.tour_id} onValueChange={(v) => set("tour_id", v)}>
                <SelectTrigger data-testid="trip-tour-select"><SelectValue placeholder="Pilih produk tour" /></SelectTrigger>
                <SelectContent>
                  {tours.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`tour-option-${t.id}`}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tour && (
                <p className="text-xs text-slate-500" data-testid="selected-tour-prices">
                  Harga/trip <span className="font-num">{formatIDR(tour.price_per_trip)}</span> · Tiket/orang <span className="font-num">{formatIDR(tour.ticket_price)}</span>
                </p>
              )}
            </div>
            <ExtrasEditor extras={form.extras} onChange={(v) => set("extras", v)} />
            <AquaSusuk form={form} set={set} aquaPrice={aquaPrice} />
            {isAdmin ? (
              <div className="space-y-1">
                <Label>Collect ke Tamu (Rp)</Label>
                <Input type="number" min={0} className="font-num" placeholder="0 = Voucher" value={form.collect || ""} onChange={(e) => set("collect", Number(e.target.value) || 0)} data-testid="trip-collect-input" />
                <p className="text-[11px] text-slate-500">Diisi jika driver menagih uang ke tamu. Kosong = Voucher (tamu sudah bayar ke perusahaan). Collect dipotong dari total bayar ke driver.</p>
              </div>
            ) : form.collect > 0 && (
              <p className="rounded-lg bg-orange-50 border border-orange-100 px-3 py-2 text-xs text-orange-800" data-testid="trip-collect-info">Collect dari tamu: <b className="font-num">{formatIDR(form.collect)}</b> (dipotong dari total bayar)</p>
            )}
            <div className="space-y-1.5">
              <Label>Catatan (opsional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="trip-notes-input" />
            </div>
          </div>
          <div className="bg-slate-950 text-white p-6 flex flex-col gap-2 text-sm md:rounded-r-lg">
            <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">Ringkasan</p>
            <Line label="Harga Trip" value={formatIDR(calc.base)} testId="calc-base" />
            <Line label={`Tiket (${Number(form.pax) || 0} org)`} value={formatIDR(calc.ticket)} testId="calc-ticket" />
            <Line label="Extra" value={formatIDR(calc.extras)} testId="calc-extras" />
            <Line label={`Aqua (${form.aqua ? Number(form.aqua_qty) || 0 : 0} pax)`} value={formatIDR(calc.aqua)} testId="calc-aqua" />
            <Line label="Total" value={formatIDR(calc.total)} strong testId="calc-total" />
            <Line label={calc.collect ? "Collect (dari tamu)" : "Pembayaran tamu"} value={calc.collect ? `- ${formatIDR(calc.collect)}` : "Voucher"} testId="calc-collect" />
            <Line label="Dibayar ke Driver" value={formatIDR(calc.payout)} testId="calc-payout" />
            <div className="flex justify-between gap-3 mt-2 rounded-lg bg-white/5 px-3 py-2 text-xs">
              <span className="text-slate-400">Susuk (terpisah)</span>
              <span className="font-num text-amber-300" data-testid="calc-susuk">{formatIDR(form.susuk)}</span>
            </div>
            <Button type="submit" disabled={saving} className="mt-auto pt-2 h-11 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="trip-submit-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Simpan Report
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
